import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { categories } from '@/mocks/homeData';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://vigorbuyspread.boats';
const pagePath = '/shopping-guide';

const webPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'VigorBuy Spreadsheet Shopping Guide',
  description: 'Browse all product categories available through VigorBuy Spreadsheet. Each category links directly to our main store where you can explore detailed product listings, compare prices, and make purchases.',
  url: `${siteUrl}${pagePath}`,
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
    { '@type': 'ListItem', position: 2, name: 'Shopping Guide', item: `${siteUrl}${pagePath}` },
  ],
};

export default function ShoppingGuide() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <Navbar />
      <main className="pt-16">
        <section className="py-16 md:py-24 bg-background-100">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex items-center gap-2 text-xs text-foreground-500 mb-4">
              <Link to="/" className="hover:text-primary-500 transition-colors">Home</Link>
              <span>/</span>
              <span className="text-foreground-700">Shopping Guide</span>
            </div>
            <div className="text-center max-w-2xl mx-auto mb-12">
              <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-900 mb-4">
                VigorBuy Spreadsheet Shopping Guide
              </h1>
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
                Browse all product categories available through VigorBuy Spreadsheet. Each category links directly
                to our main store where you can explore detailed product listings, compare prices, and make purchases.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="group flex flex-col rounded-2xl bg-background-50 border border-background-200/60 hover:border-accent-200 transition-all duration-300 overflow-hidden cursor-pointer"
                >
                  <div className="w-full aspect-[4/3] overflow-hidden">
                    <img
                      src={cat.image}
                      alt={`Shop ${cat.name} - VigorBuy Spreadsheet`}
                      className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5 flex items-center justify-between">
                    <div>
                      <h3 className="font-heading text-base font-semibold text-foreground-800 group-hover:text-primary-500 transition-colors">
                        {cat.name}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1">View all products</p>
                    </div>
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-100 group-hover:bg-accent-200 transition-colors">
                      <i className="ri-arrow-right-up-line text-accent-600"></i>
                    </div>
                  </div>
                </a>
              ))}
            </div>

            <div className="mt-16 p-8 rounded-2xl bg-primary-500 text-center">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-white mb-3">
                Can't Find What You Need?
              </h2>
              <p className="text-white/80 text-sm md:text-base mb-6 max-w-lg mx-auto">
                Visit our main store to browse the complete product catalog with thousands of items across all categories.
              </p>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl bg-white text-primary-600 text-base font-semibold hover:bg-white/90 transition-colors cursor-pointer whitespace-nowrap"
              >
                Visit Main Store
                <i className="ri-external-link-line"></i>
              </a>
            </div>

            <div className="mt-10 pt-8 border-t border-background-200/60 flex justify-center">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-500 transition-colors"
              >
                <i className="ri-arrow-left-line"></i>
                Back to Homepage
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}